/**
 * `GET /upcoming` (contract §6) — the sends the sweeper WOULD make within the horizon,
 * projected from the SAME pure decision functions the sweeper itself calls, so this list
 * can never promise a send the sweeper would refuse.
 *
 * "Run now" (`POST /run`, no step-up — §6) runs one real sweeper pass immediately and
 * reports the outcome counts; it does not (and must not) invent a preview of its own.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { formatDateTime } from "../../lib/dateTime";
import { extractApiError } from "../../lib/apiErrors";
import { LIFECYCLE } from "../../constants/testIds/lifecycle";
import lifecycleApi from "./api";
import { UPCOMING_DAYS_DEFAULT } from "./contract";

// `onRan` fires after a successful Run now pass, so the sibling history can refetch.
// `canRun`: whether the operator may run the sweeper (POST /run is settings.write); the list is
// shown to everyone who can read it, the button only to them.
export default function UpcomingSends({ api = lifecycleApi, days = UPCOMING_DAYS_DEFAULT, onRan, canRun = false }) {
  const t = useT("superadmin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [status, setStatus] = useState("loading");
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const body = await api.getUpcoming(days);
      setItems(Array.isArray(body?.items) ? body.items : []);
      setLoadError(null);
      setStatus("ready");
    } catch (e) {
      setLoadError(e);
      setStatus("error");
    }
  }, [api, days]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setRunError("");
    try {
      const result = await api.runNow();
      setRunResult(result);
      load();
      if (onRan) onRan(result);
    } catch (e) {
      setRunError(extractApiError(e, tv("errors.saveFailed"), tv));
    } finally {
      setRunning(false);
    }
  };

  if (status === "error") {
    return (
      <LoadFailure
        message={extractApiError(loadError, tv("errors.loadFailed"), tv)}
        onRetry={load}
        retryLabel={tv("actions.retry")}
        testId="lifecycle-upcoming-load-error"
        retryTestId="lifecycle-upcoming-retry"
      />
    );
  }

  return (
    <section data-testid={LIFECYCLE.upcoming} aria-busy={status === "loading"}>
      <div className="flex items-center justify-between gap-4 mb-3">
        <h3 className="text-lg font-semibold text-slate-800">{t("lifecycle.upcoming.title")}</h3>
        {canRun && (
        <Button
          type="button"
          onClick={runNow}
          disabled={running}
          data-testid={LIFECYCLE.runNow}
          variant="outline"
        >
          {running ? t("lifecycle.upcoming.running") : t("lifecycle.upcoming.runNow")}
        </Button>
        )}
      </div>

      {runResult && (
        <p data-testid={LIFECYCLE.runResult} role="status" className="text-sm text-slate-600 mb-3">
          {t("lifecycle.run.result", { sent: runResult.sent, skipped: runResult.skipped, failed: runResult.failed })}
        </p>
      )}
      {runError && <p role="alert" className="text-xs text-rose-600 mb-3">{runError}</p>}

      {items.length === 0 ? (
        <p data-testid={LIFECYCLE.upcomingEmpty} className="text-slate-400 text-sm py-4 text-center">
          {t("lifecycle.upcoming.empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li
              key={`${it.tenant_id}-${it.moment}-${i}`}
              data-testid={LIFECYCLE.upcomingRow(i)}
              data-moment={it.moment}
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-2 text-sm"
            >
              <span className="text-slate-700 truncate">{it.tenant_name}</span>
              <span className="text-slate-500 whitespace-nowrap">{t(`lifecycle.moment.${it.moment}`)}</span>
              <span className="text-slate-400 whitespace-nowrap">
                {formatDateTime(it.due_at, lang)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
